import axios from '@/config/axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useEntraAuth } from './useEntraAuth';
import { useAccountsContext } from '../contexts/AccountsContext';

export enum CustomerDocumentsStatus {
    UploadEnforced = 'UploadEnforced',
    UnderReview = 'UnderReview',
    VerificationCompleted = 'VerificationCompleted',
    VerificationAcknowledged = 'VerificationAcknowledged',
}

export type RestrictionItem = {
    restriction: string;
    isRestricted: boolean;
};

export type CustomerData = {
    destinationPort: string;
    destinationPortId: number;
    customerDocumentsStatus: CustomerDocumentsStatus;
    areNraTermsAndConditionsAccepted: boolean;
    areShippingRatesAccepted: boolean;
    areTermsAndConditionsAccepted: boolean;
    isAccountFrozen: boolean;
    isAddVehicleEnabled: boolean;
    isClientArchived: boolean;
    isDockReceiptShown: boolean;
    isEndUserUltimateDestinationAccepted: boolean;
    isBillOfSaleShown: boolean;
    isBillOfSaleAccepted: boolean;
    isProtectionPlanAccepted: boolean;
    isProtectionPlanShown: boolean;
    legacySessionInitiationToken: string;
    // Null/absent means the client is billed only by W8 Shipping and G&G Auto.
    invoiceType?: string | null;
    name: string;
    showAnnouncementsPopup: boolean;
    forceAnnouncementsPopup?: boolean;
    restrictions?: RestrictionItem[];
    restrictionUpdates?: RestrictionItem[];
};

export const useCustomerData = () => {
    const { isAuthenticated } = useEntraAuth();
    const { hasAccounts, isReady: areAccountsReady, selectedAccountId } = useAccountsContext();
    const [data, setData] = useState<CustomerData | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [errorStatus, setErrorStatus] = useState<number | null>(null);
    const isWaitingForAccounts = isAuthenticated && !areAccountsReady;
    const canFetch = isAuthenticated && areAccountsReady && hasAccounts && selectedAccountId !== null;

    // A background refresh (e.g. triggered by a contract accepted in another tab) must not
    // toggle isLoading — several consumers unmount into a spinner on it — and must fail
    // silently, keeping the stale data instead of surfacing errors the user didn't cause.
    // Every call issues a fresh request (a post-mutation refresh must never reuse a
    // response snapshotted before the mutation). A response may write state only if it is
    // newer than the data already displayed AND no foreground request was issued after it
    // (foreground refreshes follow mutations, so older snapshots must never override them).
    // A failed request never voids data written by an earlier successful one.
    const issueSeqRef = useRef(0);
    const writtenSeqRef = useRef(0);
    const latestForegroundSeqRef = useRef(0);

    const fetchData = useCallback(
        async (options?: { background?: boolean }) => {
            if (!canFetch) {
                return;
            }
            const isBackground = options?.background === true;
            const requestSeq = ++issueSeqRef.current;
            if (!isBackground) {
                latestForegroundSeqRef.current = requestSeq;
                setIsLoading(true);
                setErrorStatus(null);
            }
            const mayWrite = () => requestSeq > writtenSeqRef.current && requestSeq >= latestForegroundSeqRef.current;
            try {
                const { data } = await axios.get('/api/customer-details', { disableToast: isBackground });
                if (mayWrite()) {
                    writtenSeqRef.current = requestSeq;
                    setData(data);
                    setErrorStatus(null);
                }
            } catch (error) {
                if (!isBackground && mayWrite()) {
                    if (error.response) {
                        setErrorStatus(error.response.status);
                    } else {
                        setErrorStatus(500);
                    }
                }
            } finally {
                // Only the latest foreground request may clear the flag: a superseded one
                // finishing early must not hide the loading state of a newer in-flight
                // request (or undo the reset done on an account/auth change).
                if (!isBackground && requestSeq === latestForegroundSeqRef.current) {
                    // Every exit path already clears the flag, so the spinner cannot get stuck.
                    // react-doctor-disable-next-line no-loading-flag-reset-outside-finally
                    setIsLoading(false);
                }
            }
        },
        [canFetch]
    );

    useEffect(() => {
        if (canFetch) {
            fetchData();
            return;
        }

        // Invalidate all in-flight requests so a late response can't overwrite this reset.
        issueSeqRef.current++;
        writtenSeqRef.current = issueSeqRef.current;
        latestForegroundSeqRef.current = issueSeqRef.current;
        setData(null);
        setErrorStatus(null);
        setIsLoading(isWaitingForAccounts);
    }, [canFetch, fetchData, isWaitingForAccounts]);

    const updateShowAnnouncementsPopup = useCallback(async (showAnnouncementsPopup: boolean) => {
        setData(prev => (prev ? { ...prev, showAnnouncementsPopup: false } : null));
        try {
            await axios.patch('/api/customer-details', { showAnnouncementsPopup });
            setData(prev => (prev ? { ...prev, showAnnouncementsPopup } : null));
        } catch {}
    }, []);

    return {
        isLoading: isWaitingForAccounts || isLoading,
        data,
        errorStatus,
        fetchData,
        updateShowAnnouncementsPopup,
    };
};
